/**
 * Campaign saves in the browser (M1.1, docs/13-architecture.md "Saves").
 *
 * The bytes are the sim's (`campaign::save_campaign`, one JSON document with a
 * format tag and its own hash); this file is only *where they go*. Three
 * places, in ascending order of the player's control over them:
 *
 * 1. **Autosave** to the origin's private file system (OPFS), falling back to
 *    IndexedDB where OPFS has no writable stream on the main thread. Browser
 *    storage is evictable, which is why the design insists on the next two.
 * 2. **Export to file**: a download the player owns.
 * 3. **Import from file**: the same document back in.
 *
 * Nothing here reads a save's contents; the worker's `load` is what vouches
 * for them, and a refusal comes back as a sentence rather than a silence.
 */

const AUTOSAVE_NAME = 'campaign-autosave.json';
const DB_NAME = 'yield-saves';
const DB_STORE = 'saves';

export type SaveStore = 'opfs' | 'indexeddb' | 'none';

/** Where autosaves are going on this page, decided at first use. */
let store: SaveStore | null = null;

async function opfsRoot(): Promise<FileSystemDirectoryHandle | null> {
  try {
    if (!navigator.storage?.getDirectory) return null;
    return await navigator.storage.getDirectory();
  } catch {
    return null;
  }
}

async function opfsWrite(json: string): Promise<boolean> {
  const root = await opfsRoot();
  if (!root) return false;
  try {
    const handle = await root.getFileHandle(AUTOSAVE_NAME, { create: true });
    // Safari has no `createWritable` on the main thread; the fallback below
    // takes over rather than the autosave silently not happening.
    if (typeof (handle as { createWritable?: unknown }).createWritable !== 'function') return false;
    const writable = await handle.createWritable();
    await writable.write(json);
    await writable.close();
    return true;
  } catch {
    return false;
  }
}

async function opfsRead(): Promise<string | null> {
  const root = await opfsRoot();
  if (!root) return null;
  try {
    const handle = await root.getFileHandle(AUTOSAVE_NAME);
    const file = await handle.getFile();
    return await file.text();
  } catch {
    return null;
  }
}

async function opfsClear(): Promise<void> {
  const root = await opfsRoot();
  if (!root) return;
  try {
    await root.removeEntry(AUTOSAVE_NAME);
  } catch {
    /* nothing to clear */
  }
}

function idb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') return resolve(null);
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(DB_STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
  });
}

async function idbWrite(json: string): Promise<boolean> {
  const db = await idb();
  if (!db) return false;
  return new Promise((resolve) => {
    const tx = db.transaction(DB_STORE, 'readwrite');
    tx.objectStore(DB_STORE).put(json, AUTOSAVE_NAME);
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => resolve(false);
  });
}

async function idbRead(): Promise<string | null> {
  const db = await idb();
  if (!db) return null;
  return new Promise((resolve) => {
    const tx = db.transaction(DB_STORE, 'readonly');
    const request = tx.objectStore(DB_STORE).get(AUTOSAVE_NAME);
    request.onsuccess = () => resolve(typeof request.result === 'string' ? request.result : null);
    request.onerror = () => resolve(null);
  });
}

async function idbClear(): Promise<void> {
  const db = await idb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    const tx = db.transaction(DB_STORE, 'readwrite');
    tx.objectStore(DB_STORE).delete(AUTOSAVE_NAME);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}

/** Write the autosave. Returns where it went, or `none` if nowhere would take it. */
export async function writeAutosave(json: string): Promise<SaveStore> {
  if (store !== 'indexeddb' && (await opfsWrite(json))) {
    store = 'opfs';
    return store;
  }
  if (await idbWrite(json)) {
    store = 'indexeddb';
    return store;
  }
  store = 'none';
  return store;
}

/** The autosave, if there is one, from whichever store took it. */
export async function readAutosave(): Promise<{ json: string; store: SaveStore } | null> {
  const fromOpfs = await opfsRead();
  if (fromOpfs) return { json: fromOpfs, store: 'opfs' };
  const fromIdb = await idbRead();
  if (fromIdb) return { json: fromIdb, store: 'indexeddb' };
  return null;
}

export async function clearAutosave(): Promise<void> {
  await Promise.all([opfsClear(), idbClear()]);
}

export function autosaveStore(): SaveStore | null {
  return store;
}

/** Export: a download the player owns. */
export function downloadSave(json: string, label: string): void {
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `yield-${label.replace(/[^\w.-]+/g, '_')}.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Import: pick a file, resolve to its text. */
export function pickSaveFile(): Promise<string | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.onchange = async () => {
      const file = input.files?.[0];
      resolve(file ? await file.text() : null);
    };
    input.oncancel = () => resolve(null);
    input.click();
  });
}
